/**
 * Panel for managing finding remediation: mark as resolved, trigger rescan,
 * mark as false positive, or accept risk.
 *
 * Only visible to users with remediation_lead role or higher.
 */

import { useState } from 'react';
import type { Finding, FindingStatus } from '@/types/finding';
import { useUpdateFindingStatus, useTriggerRescan } from '@/hooks/useFindings';

interface RemediationPanelProps {
  finding: Finding;
  canManage: boolean;
  onStatusUpdated?: (updated: Finding) => void;
}

const statusActions: { status: FindingStatus; label: string; style: string; description: string }[] = [
  {
    status: 'resolved',
    label: 'Mark Resolved',
    style: 'btn-primary',
    description: 'Mark this finding as fixed. A rescan will verify the fix.',
  },
  {
    status: 'false_positive',
    label: 'False Positive',
    style: 'btn-secondary',
    description: 'Mark this as a false positive — the scanner incorrectly flagged it.',
  },
  {
    status: 'accepted_risk',
    label: 'Accept Risk',
    style: 'btn-secondary',
    description: 'Acknowledge the risk and accept it without remediation.',
  },
];

export default function RemediationPanel({ finding, canManage, onStatusUpdated }: RemediationPanelProps) {
  const updateStatus = useUpdateFindingStatus();
  const triggerRescan = useTriggerRescan();
  const [confirming, setConfirming] = useState<FindingStatus | null>(null);

  if (!canManage) return null;

  // Don't show for already-resolved findings.
  if (finding.status === 'resolved') {
    return (
      <div className="rounded-md border border-green-200 bg-green-50 p-4">
        <p className="text-sm font-medium text-green-800">
          This finding has been resolved.
        </p>
        <button
          onClick={() => triggerRescan.mutate(finding.id)}
          disabled={triggerRescan.isPending}
          className="btn-secondary mt-2"
        >
          {triggerRescan.isPending ? 'Triggering...' : 'Verify with Rescan'}
        </button>
      </div>
    );
  }

  function handleConfirm(statusToSet: FindingStatus) {
    updateStatus.mutate(
      { findingId: finding.id, payload: { status: statusToSet } },
      {
        onSuccess: (updated) => {
          setConfirming(null);
          onStatusUpdated?.(updated);
        },
      },
    );
  }

  return (
    <div className="space-y-3 rounded-md border p-4">
      <h3 className="text-sm font-semibold text-gray-700">Remediation Actions</h3>

      {statusActions.map((action) => (
        <div key={action.status} className="flex items-center justify-between gap-4">
          <p className="text-sm text-gray-600">{action.description}</p>
          {confirming === action.status ? (
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => handleConfirm(action.status)}
                disabled={updateStatus.isPending}
                className="btn-primary whitespace-nowrap"
              >
                {updateStatus.isPending ? 'Saving...' : 'Confirm'}
              </button>
              <button
                onClick={() => setConfirming(null)}
                disabled={updateStatus.isPending}
                className="btn-secondary whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(action.status)}
              disabled={updateStatus.isPending}
              className={`${action.style} shrink-0 whitespace-nowrap`}
            >
              {action.label}
            </button>
          )}
        </div>
      ))}

      {/* Rescan button */}
      <div className="flex items-center justify-between gap-4 border-t pt-3">
        <p className="text-sm text-gray-600">
          Trigger a targeted rescan to re-check this specific vulnerability.
        </p>
        <button
          onClick={() => triggerRescan.mutate(finding.id)}
          disabled={triggerRescan.isPending}
          className="btn-secondary whitespace-nowrap"
        >
          {triggerRescan.isPending ? 'Scanning...' : 'Rescan'}
        </button>
      </div>
    </div>
  );
}
